import { useQuery } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import type { RouterOutputs } from "@/trpc";
import { useMemo } from "react";
import { getWeekKey, parseWeekKey, type WeekKey } from "../utils/week-utils";

export type { WeekKey };

// Use explicit types from router outputs
export type WeeklyShiftGroup =
  RouterOutputs["timeEntry"]["getShiftsNeedingEntries"][number];

export type ShiftNeedingTimeEntry = WeeklyShiftGroup["shifts"][number];

/**
 * Hook to fetch shifts that need time entries for the current provider.
 * Returns shifts grouped by week (Monday-Sunday).
 */
export function useTimeEntryShifts(weekKey?: WeekKey) {
  const trpc = useTRPC();

  const weekBounds = weekKey ? parseWeekKey(weekKey) : undefined;

  const {
    data: weeklyGroups,
    isLoading,
    error,
    refetch,
  } = useQuery(
    trpc.timeEntry.getShiftsNeedingEntries.queryOptions({
      weekStart: weekBounds?.start,
    }),
  );

  // Create a map of week keys to shift groups for easy lookup
  const shiftsByWeek = useMemo(() => {
    const map = new Map<WeekKey, WeeklyShiftGroup>();
    if (weeklyGroups) {
      for (const group of weeklyGroups) {
        map.set(group.weekKey, group);
      }
    }
    return map;
  }, [weeklyGroups]);

  // Get sorted week keys (most recent first)
  const sortedWeekKeys = useMemo(() => {
    if (!weeklyGroups) {
      return [] as string[];
    }
    return weeklyGroups.map((g) => g.weekKey);
  }, [weeklyGroups]);

  // Get current week's shifts
  const currentWeekKey = getWeekKey(new Date());
  const currentWeekGroup = shiftsByWeek.get(currentWeekKey);

  // Calculate totals
  const totalShiftsNeedingEntries = useMemo(() => {
    if (!weeklyGroups) {
      return 0;
    }
    return weeklyGroups.reduce((sum, g) => sum + g.totalShifts, 0);
  }, [weeklyGroups]);

  const totalRejectedEntries = useMemo(() => {
    if (!weeklyGroups) {
      return 0;
    }
    return weeklyGroups.reduce((sum, g) => sum + g.rejectedCount, 0);
  }, [weeklyGroups]);

  return {
    weeklyGroups: (weeklyGroups ?? []) as WeeklyShiftGroup[],
    shiftsByWeek,
    sortedWeekKeys,
    currentWeekKey,
    currentWeekGroup,
    totalShiftsNeedingEntries,
    totalRejectedEntries,
    isLoading,
    error,
    refetch,
  };
}
